import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

const API = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const AUDIENCE_SIZES = [
  '1K - 10K',
  '10K - 50K',
  '50K - 100K',
  '100K - 500K',
  '500K+',
];

const PLATFORMS = [
  'YouTube',
  'Twitter / X',
  'Discord',
  'Newsletter',
  'TikTok',
  'Instagram',
  'Podcast',
  'Website / Blog',
  'Other',
];

export const PartnershipPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [brand, setBrand] = useState('');
  const [audienceSize, setAudienceSize] = useState('10K - 50K');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [website, setWebsite] = useState('');
  const [about, setAbout] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !about.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true); setError('');
    try {
      const resp = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          inquiryType: 'influencer',
          message: `Partnership Application\n\nBrand: ${brand}\nAudience Size: ${audienceSize}\nPlatforms: ${platforms.length > 0 ? platforms.join(', ') : 'N/A'}\nWebsite: ${website || 'N/A'}\n\n${about.trim()}`,
        }),
      });
      if (!resp.ok) throw new Error('Failed to send');
      setSuccess(true);
    } catch {
      setError('Failed to submit application. Please email us at support@wealthfinancialsystems.com');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '0.75rem 1rem', borderRadius: 8,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
    color: '#f9fafb', fontSize: '0.95rem', outline: 'none', transition: 'border-color 0.2s',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#cbd5e1', marginBottom: '0.4rem',
  };

  const focusHandler = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    e.target.style.borderColor = '#ec4899';
  };
  const blurHandler = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    e.target.style.borderColor = 'rgba(255,255,255,0.15)';
  };

  return (
    <div className="lp-page">
      {/* Nav */}
      <nav className="lp-nav">
        <Link to="/" className="lp-nav-brand" style={{ textDecoration: 'none' }}>
          <div className="lp-nav-logo">W</div>
          <span className="lp-nav-title">Wealth Financial Systems</span>
        </Link>
        <div className="lp-nav-actions">
          <Link to="/influencer" className="lp-btn" style={{ color: '#94a3b8', textDecoration: 'none' }}>About Partnership</Link>
          <Link to="/login" className="lp-btn lp-btn-primary" style={{ background: 'linear-gradient(135deg, #ec4899, #be185d)' }}>Sign In</Link>
        </div>
      </nav>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '4rem 2rem' }}>
        <div style={{ display: 'inline-block', padding: '0.3rem 1rem', borderRadius: 999, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', background: 'rgba(236,72,153,0.1)', color: '#f472b6', border: '1px solid rgba(236,72,153,0.3)', marginBottom: '1rem' }}>
          PARTNERSHIP APPLICATION
        </div>
        <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '0.5rem', color: '#f59e0b' }}>Apply to Partner With Us</h1>
        <p style={{ color: '#cbd5e1', marginBottom: '2.5rem', fontSize: '1rem', lineHeight: 1.6 }}>
          Tell us about yourself and your audience. We'll review your application and schedule a call within 2 business days.
        </p>

        {success ? (
          <div style={{ textAlign: 'center', padding: '3rem 2rem', background: 'rgba(236,72,153,0.08)', border: '1px solid rgba(236,72,153,0.3)', borderRadius: 12 }}>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#f472b6', marginBottom: '0.75rem' }}>Application Received</div>
            <p style={{ color: '#cbd5e1', marginBottom: '1.5rem' }}>
              Thank you, {name}! We'll review your application and get back to you within 2 business days to schedule a call.
            </p>
            <Link to="/influencer" className="lp-btn lp-btn-outline lp-btn-lg" style={{ textAlign: 'center', borderColor: 'rgba(236,72,153,0.4)', color: '#f472b6' }}>Back to Partnership Info</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Your Name *</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} style={inputStyle}
                onFocus={focusHandler} onBlur={blurHandler} placeholder="Your name" />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Email *</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle}
                onFocus={focusHandler} onBlur={blurHandler} placeholder="you@example.com" />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Brand / Channel Name</label>
              <input type="text" value={brand} onChange={e => setBrand(e.target.value)} style={inputStyle}
                onFocus={focusHandler} onBlur={blurHandler} placeholder="e.g. TradeWithJohn, Options Daily Newsletter" />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Audience Size</label>
              <select value={audienceSize} onChange={e => setAudienceSize(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
                onFocus={focusHandler as any} onBlur={blurHandler as any}>
                {AUDIENCE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Platforms (select all that apply)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.4rem' }}>
                {PLATFORMS.map(p => {
                  const selected = platforms.includes(p);
                  return (
                    <button type="button" key={p} onClick={() => setPlatforms(prev => selected ? prev.filter(x => x !== p) : [...prev, p])}
                      style={{
                        padding: '0.45rem 0.9rem', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
                        background: selected ? 'rgba(236,72,153,0.15)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${selected ? 'rgba(236,72,153,0.5)' : 'rgba(255,255,255,0.12)'}`,
                        color: selected ? '#f472b6' : '#94a3b8',
                        transition: 'all 0.15s',
                      }}>
                      {selected ? '\u2713 ' : ''}{p}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Website / Channel URL</label>
              <input type="url" value={website} onChange={e => setWebsite(e.target.value)} style={inputStyle}
                onFocus={focusHandler} onBlur={blurHandler} placeholder="https://youtube.com/@yourchannel" />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Tell Us About Yourself *</label>
              <textarea value={about} onChange={e => setAbout(e.target.value)} rows={4}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 100 }}
                onFocus={focusHandler as any} onBlur={blurHandler as any}
                placeholder="What kind of content do you create? What trading topics does your audience care about? Why are you interested in partnering?" />
            </div>

            {error && (
              <div style={{ padding: '0.75rem', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: '0.9rem', marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={submitting}
              className="lp-btn lp-btn-primary lp-btn-lg"
              style={{ width: '100%', textAlign: 'center', background: 'linear-gradient(135deg, #ec4899, #be185d)', opacity: submitting ? 0.6 : 1, cursor: submitting ? 'wait' : 'pointer' }}>
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>

            <p style={{ marginTop: '1rem', textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>
              Not sure yet? <Link to="/influencer" style={{ color: '#f472b6' }}>Learn more about the partnership program</Link>
            </p>
          </form>
        )}
      </div>

      {/* Footer */}
      <footer style={{ textAlign: 'center', padding: '2rem', fontSize: '0.8rem', color: '#475569', borderTop: '1px solid rgba(255,255,255,0.04)', marginTop: 'auto' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none', marginRight: '1.5rem' }}>Home</Link>
          <Link to="/influencer" style={{ color: '#64748b', textDecoration: 'none', marginRight: '1.5rem' }}>Influencer</Link>
          <Link to="/enterprise" style={{ color: '#64748b', textDecoration: 'none', marginRight: '1.5rem' }}>Enterprise</Link>
          <Link to="/terms" style={{ color: '#64748b', textDecoration: 'none' }}>Terms</Link>
        </div>
        &copy; {new Date().getFullYear()} Wealth Financial Systems. All rights reserved.
      </footer>
    </div>
  );
};
